<template>
  <div class="dispatch-layout" v-if="isRouterAlive">
    <aside class="dispatch-sidebar">
      <div class="sidebar-brand">
        <img :src="logo" alt="Logo" class="brand-logo" />
        <div>
          <p class="brand-name">{{ projectName }}</p>
          <p class="brand-version">运营调度后台 · v0.1</p>
        </div>
      </div>

      <nav class="sidebar-nav">
        <div
          v-for="menu in menus"
          :key="menu.id"
          class="nav-item"
          :class="{ active: isActiveMenu(menu) }"
          @click="navigateToMenu(menu)"
        >
          <a-icon :type="menu.icon || 'setting'" />
          <span class="nav-label">{{ menu.name }}</span>
          <span v-if="menu.badge" class="nav-badge">{{ menu.badge }}</span>
        </div>
      </nav>

      <div class="sidebar-user">
        <div class="user-avatar">{{ userInitial }}</div>
        <div class="user-info">
          <p class="user-name">{{ displayName }}</p>
          <p class="user-status"><span class="status-dot" />在线</p>
        </div>
      </div>
    </aside>

    <main class="dispatch-main">
      <header class="dispatch-header">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="header-right">
          <span class="header-time">{{ currentTime }}</span>
          <button class="logout-btn" @click="logout">
            <a-icon type="logout" />
            退出
          </button>
        </div>
      </header>

      <div class="dispatch-content">
        <div class="dispatch-content-inner">
          <router-view />
        </div>
      </div>
    </main>

    <a-drawer
      title="修改密码"
      placement="right"
      :closable="true"
      :visible="passwordVisible"
      @close="passwordVisible = false"
      width="400"
    >
      <EditPassword />
    </a-drawer>
  </div>
</template>

<script>
import EditPassword from "../components/usermanage/editPassword.vue";
import { dispatchApi } from "../components/dispatch/dispatchApi";
import { resolveDispatchSidebarMenus } from "../components/dispatch/dispatchSidebarMenus";
import brandLogo from "./brandLogo";

export default {
  name: "Home",
  data() {
    return {
      isRouterAlive: true,
      passwordVisible: false,
      currentTime: "",
      clockTimer: null,
      pendingCount: 0,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    projectName() {
      return this.$store.state.projectName || "安心陪护";
    },
    logo() {
      return brandLogo;
    },
    menus() {
      return this.$store.state.menus || [];
    },
    pageTitle() {
      return (this.$route.meta && this.$route.meta.pageTitle) || this.$route.name || "运营调度后台";
    },
    displayName() {
      const u = this.user;
      return (u && u.name) || "调度员001";
    },
    userInitial() {
      return this.displayName.charAt(0);
    },
  },
  beforeCreate() {
    const user = this.$store.state.user;
    if (!user) {
      this.$router.push("/login");
    }
  },
  created() {
    this.initMenus();
    this.updatePendingBadge();
    this.tickClock();
    this.clockTimer = setInterval(this.tickClock, 1000);
    this.$root.$on("dispatch-stats-update", this.onStatsUpdate);
  },
  beforeDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer);
    this.$root.$off("dispatch-stats-update", this.onStatsUpdate);
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.initMenus();
      },
    },
    $route() {
      this.updatePendingBadge();
    },
  },
  methods: {
    tickClock() {
      const now = new Date();
      this.currentTime = now.toLocaleString("zh-CN", { hour12: false });
    },
    onStatsUpdate(stats) {
      this.pendingCount = stats.pending || 0;
      this.initMenus();
    },
    async updatePendingBadge() {
      try {
        const stats = await dispatchApi.stats();
        this.pendingCount = stats.pending || 0;
      } catch (e) {
        this.pendingCount = 0;
      }
      this.initMenus();
    },
    initMenus() {
      const badge = this.pendingCount > 0 ? String(this.pendingCount) : null;
      let menus = resolveDispatchSidebarMenus(this.user, true);
      menus = menus.map((menu) =>
        menu.path === "/dispatch/orders" ? { ...menu, badge } : menu
      );
      this.$store.commit("setMenus", menus);
    },
    isActiveMenu(menu) {
      return this.$route.path === menu.path;
    },
    navigateToMenu(menu) {
      if (menu.path) this.$router.push(menu.path);
    },
    logout() {
      this.$confirm({
        title: "提示",
        content: "确认退出登录吗？",
        onOk: () => {
          this.$store.dispatch("LogOut").then(() => {
            this.$router.push("/login");
          });
        },
      });
    },
  },
  components: {
    EditPassword,
  },
};
</script>

<style scoped>
.dispatch-layout {
  display: flex;
  height: 100vh;
  background: #f1f5f9;
}

.dispatch-sidebar {
  width: 220px;
  background: #fff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1rem;
  border-bottom: 1px solid #f1f5f9;
}

.brand-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;
}

.brand-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.brand-version {
  margin: 0.125rem 0 0;
  font-size: 0.6875rem;
  color: #94a3b8;
}

.sidebar-nav {
  flex: 1;
  padding: 0.75rem 0.5rem;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  margin-bottom: 0.125rem;
  border-radius: 0.5rem;
  cursor: pointer;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
}

.nav-item:hover {
  background: #f8fafc;
  color: #0f172a;
}

.nav-item.active {
  background: #eff6ff;
  color: #0082d4;
}

.nav-label {
  flex: 1;
}

.nav-badge {
  background: #ef4444;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}

.sidebar-user {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #0082d4;
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-name {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f172a;
}

.user-status {
  margin: 0.125rem 0 0;
  font-size: 0.6875rem;
  color: #16a34a;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
}

.dispatch-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}

.dispatch-header {
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  flex-shrink: 0;
}

.page-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-time {
  font-size: 0.8125rem;
  color: #64748b;
  font-family: ui-monospace, monospace;
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.8125rem;
  color: #475569;
  transition: border-color 0.15s, color 0.15s;
}

.logout-btn:hover {
  border-color: #0082d4;
  color: #0082d4;
}

.dispatch-content {
  flex: 1;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.dispatch-content-inner {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dispatch-content-inner > * {
  flex: 1;
  min-height: 0;
}
</style>
